import { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { ApiRequestError } from '@/api/errors';
import { payApiClient } from '@/api/client';
import { TagsField } from '@/features/operations/filters/TagsField';
import type { AdminApiCredential } from '@/api/models';

interface CredentialIpAllowlistDialogProps {
  open: boolean;
  credential: AdminApiCredential;
  onClose: () => void;
  onSaved: (credential: AdminApiCredential) => void;
}

/** `PUT /v1/admin/api-credentials/{id}/ip-allowlist` — replaces `ipAllowlist` in full; empty accepts any source IP. */
export function CredentialIpAllowlistDialog({ open, credential, onClose, onSaved }: CredentialIpAllowlistDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const [ipAllowlist, setIpAllowlist] = useState<string[]>(credential.ipAllowlist);
  const [accessDenied, setAccessDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setIpAllowlist(credential.ipAllowlist);
      setAccessDenied(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const updated = await payApiClient.updateAdminApiCredentialIpAllowlist(credential.id, { ipAllowlist });
      notify(translate('pay.credentials.ipAllowlistDialog.success'), { type: 'success' });
      onSaved(updated);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.errorCode === 'access_denied') {
        setAccessDenied(true);
      } else {
        const message = caught instanceof ApiRequestError ? caught.message : undefined;
        notify(message ?? translate('pay.errorState.body'), { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{translate('pay.credentials.ipAllowlistDialog.title')}</DialogTitle>
      <DialogContent>
        {accessDenied ? (
          <AccessDenied />
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TagsField
              label={translate('pay.credentials.ipAllowlistDialog.fieldIpAllowlist')}
              helperText={translate('pay.credentials.ipAllowlistDialog.fieldIpAllowlistHelp')}
              values={ipAllowlist}
              onChange={setIpAllowlist}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('pay.credentials.ipAllowlistDialog.cancel')}</Button>
        {!accessDenied && (
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {translate('pay.credentials.ipAllowlistDialog.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
